import FolderIcon from '@mui/icons-material/Folder';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type React from 'react';
import type { TicketHistoryMessage } from 'services/types/ticket';
import textToReactNodes from 'utils/textToReactNodes';

import MessageWrapper from './MessageWrapper';

interface Props {
  message: TicketHistoryMessage;
  history: TicketHistoryMessage[];
}

const ToolResponseComponent: React.FC<Props> = ({ message, history }) => {
  if (!message.toolCallId) {
    return (
      <MessageWrapper caption="Respuesta de herramienta" align="left">
        <Typography color="error">Mensaje de herramienta sin ID de llamada</Typography>
      </MessageWrapper>
    );
  }

  // Find the corresponding tool call in the history
  const toolCall = history.flatMap((msg) => msg.toolCalls || []).find((call) => call.id === message.toolCallId);

  if (!toolCall) {
    return (
      <MessageWrapper caption="Respuesta de herramienta" align="left">
        <Typography color="error">No se encontró la llamada de herramienta correspondiente</Typography>
      </MessageWrapper>
    );
  }

  const getToolDisplayName = (toolName: string) => {
    switch (toolName) {
      case 'listDir':
        return 'Resultado: Listar directorio';
      case 'readFile':
        return 'Resultado: Leer archivo';
      case 'editFile':
        return 'Resultado: Editar archivo';
      case 'grepSearch':
        return 'Resultado: Búsqueda con regex';
      case 'fileSearch':
        return 'Resultado: Búsqueda de archivos';
      case 'executeQuery':
        return 'Resultado: Consulta SQL';
      case 'getDatabaseSchema':
        return 'Resultado: Esquema de base de datos';
      case 'getClientInfo':
        return 'Resultado: Información de cliente';
      case 'getBusinessUnitDetails':
        return 'Resultado: Detalles de unidad de negocio';
      case 'requestRecommendation':
        return 'Resultado: Recomendación de request al backend';
      case 'readLogs':
        return 'Resultado: Leer logs';
      case 'markAnalysisComplete':
        return 'Resultado: Análisis completado';
      case 'editBusinessUnitPrompt':
        return 'Resultado: Editar prompt de Business Unit';
      default:
        return `Resultado: ${toolName}`;
    }
  };

  const isCodeOrStructuredOutput = (content: string) => {
    // Check if content looks like code, JSON, or structured data
    return (
      content.includes('```') ||
      content.includes('{') ||
      content.includes('SELECT') ||
      content.includes('CREATE') ||
      content.includes('|') ||
      content.split('\n').length > 5
    );
  };

  const parseDirectoryListing = (content: string) => {
    try {
      return JSON.parse(content) as { name?: string; type?: 'file' | 'directory'; size?: number; error?: string }[];
    } catch (_error) {
      return [];
    }
  };

  // Special handling for listDir results
  if (toolCall.function.name === 'listDir' && typeof message.content === 'string') {
    const directoryItems = parseDirectoryListing(message.content);

    return (
      <MessageWrapper caption={getToolDisplayName(toolCall.function.name)} align="left">
        <Box>
          {directoryItems.length > 0 ? (
            <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1 }}>
              {directoryItems.map((item, index) => (
                <ListItem key={`${item.name}-${index + 1}-${item.name?.length}`} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {item.type === 'directory' ? (
                      <FolderIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    ) : (
                      <FileIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                          }}
                        >
                          {item.name} {item.error && `(Error: ${item.error})`}
                        </Typography>
                        {item.type === 'file' && item.size && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              ml: 1,
                            }}
                          >
                            {(item.size / 1000).toFixed(2)} KB
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                overflow: 'auto',
              }}
            >
              {message.content}
            </Paper>
          )}
        </Box>
      </MessageWrapper>
    );
  }

  if (toolCall.function.name === 'readFile' && typeof message.content === 'string') {
    const fileContent = message.content;
    const { content } = JSON.parse(fileContent) as { content: string };

    return (
      <MessageWrapper caption={getToolDisplayName(toolCall.function.name)} align="left">
        <Box sx={{ maxHeight: '500px', overflow: 'auto' }}>{textToReactNodes(`\`\`\`\n${content}\n\`\`\``)}</Box>
      </MessageWrapper>
    );
  }

  if (Array.isArray(message.content)) {
    return (
      <MessageWrapper caption={getToolDisplayName(toolCall.function.name)} align="left">
        <Box>{JSON.stringify(message.content)}</Box>
      </MessageWrapper>
    );
  }

  return (
    <MessageWrapper caption={getToolDisplayName(toolCall.function.name)} align="left">
      <Box>
        {isCodeOrStructuredOutput(message.content) ? (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              overflow: 'auto',
              maxHeight: '300px',
            }}
          >
            {message.content}
          </Paper>
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {message.content}
          </Typography>
        )}
      </Box>
    </MessageWrapper>
  );
};

export default ToolResponseComponent;
